import { Box, TextField, Tooltip, IconButton, useTheme } from '@mui/material';
import { alpha } from '@mui/material/styles';
import InfoOutlinedIcon from '@mui/icons-material/InfoOutlined';
import React from 'react';

// Consistent styles for labels and notes
export const createItemstyle: React.CSSProperties = {
  display: 'block',
  marginBottom: '6px',
  marginLeft: '8px', // space out labels from inputs
  marginRight: '8px', // space out labels from inputs
  width: '50%', // make labels full width
};

// Consistent full-width input
export const createItemstyleFW: React.CSSProperties = {
  display: 'block',
  width: '100%',
  padding: '8px',
  borderRadius: '4px',
  border: '1px solid #ccc',
  marginBottom: '16px',
};

interface GenericItemNameInputProps {
  value: string;
  onChange: (newValue: string) => void;
  error?: boolean;
  helperText?: string;
  required?: boolean;
}

const GenericItemNameInput: React.FC<GenericItemNameInputProps> = ({
  value,
  onChange,
  error,
  helperText,
  required = false,
}) => {
  const theme = useTheme();

  return (
    <Box sx={{ display: 'flex', flexDirection: 'row', alignItems: 'flex-end' }}>
      <Tooltip title="Enter the generic name of the item, not including its primary attribute. e.g. 'PVC SCH 40' not '3/4&quot; PVC SCH 40'">
        <IconButton
          size="small"
          sx={{
            color:
              theme.palette.mode === 'dark'
                ? theme.palette.primary.light
                : theme.palette.primary.main,
            mr: 1,
            mb: 2,
          }}
        >
          <InfoOutlinedIcon />
        </IconButton>
      </Tooltip>
      <TextField
        fullWidth
        required={required}
        error={error}
        label="Item Name"
        value={value}
        onChange={e => onChange(e.target.value)}
        sx={{
          '& .MuiInputBase-input': {
            color: theme.palette.text.primary,
            backgroundColor:
              theme.palette.mode === 'dark' ? theme.palette.background.paper : 'white',
          },
          '& .MuiInputLabel-root': {
            color:
              theme.palette.mode === 'dark'
                ? alpha(theme.palette.common.white, 0.7)
                : alpha(theme.palette.common.black, 0.6),
          },
          '& .MuiOutlinedInput-root': {
            backgroundColor:
              theme.palette.mode === 'dark'
                ? alpha(theme.palette.background.paper, 0.15)
                : theme.palette.background.paper,
            '& fieldset': {
              borderColor:
                theme.palette.mode === 'dark' ? 'rgba(255, 255, 255, 0.23)' : 'rgba(0, 0, 0, 0.23)',
            },
            '&:hover fieldset': {
              borderColor: theme.palette.primary.main,
            },
            '&.Mui-focused fieldset': {
              borderColor: theme.palette.primary.main,
            },
          },
          '& .MuiFormHelperText-root': {
            fontSize: '0.875rem',
            fontWeight: 500,
            color: error ? theme.palette.error.main : theme.palette.text.secondary,
            marginLeft: '8px',
          },
        }}
      />
    </Box>
  );
};

export default React.memo(GenericItemNameInput);
